import { useCallback, useEffect, useState } from 'react';

const PREFETCH_MARGIN = '300px';

interface UseInfiniteScrollSentinelOptions {
  onReachEnd: () => void;
  /** Pass `hasNextPage && !isFetchingNextPage`; see the note on re-observing. */
  enabled: boolean;
  rootMargin?: string;
}

// Calls `onReachEnd` when the sentinel scrolls into view. `root` stays the
// viewport: a scrolling ancestor already clips the sentinel out of it.
// For page-level lists; a table that scrolls its own body uses CustomTable's
// onReachEnd instead, having no element in the page flow to observe.
export const useInfiniteScrollSentinel = ({ onReachEnd, enabled, rootMargin = PREFETCH_MARGIN }: UseInfiniteScrollSentinelOptions) => {
  // State, not a ref: attaching the node has to re-run the effect below.
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);

  const sentinelRef = useCallback((node: HTMLElement | null) => setSentinel(node), []);

  useEffect(() => {
    // Disconnected rather than guarded inside the callback, so at the end of a
    // list there is no live observer at all and no path back to a fetch.
    if (!enabled || !sentinel || typeof IntersectionObserver === 'undefined') return;

    // Observers fire on crossings, so a sentinel still visible after a short
    // page needs the re-observe that toggling `enabled` forces.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onReachEnd();
      },
      { rootMargin },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, onReachEnd, rootMargin, sentinel]);

  return sentinelRef;
};
